// ============================================================================
// State Management - Main Entry Point
// ============================================================================

import { auth } from 'app/auth/auth.js';
import { EventEmitter } from './event-emitter.js';
import { ObservableStore } from './observable-store.js';
import { PersistentStore } from './persistent-store.js';
import { ClientStore } from './client-store.js';
import { GroupsStore } from './groups-store.js';
import { ProfilesStore } from './profiles-store.js';
import { DropdownStore } from './dropdown-store.js';
import { NotesStore } from './notes-store.js';

class StateManager extends EventEmitter {
    constructor() {
        super();
        this.stores = new Map();
        this.user = null;
        this.initialized = false;
        this._initPromise = null;
    }

    /**
     * Idempotent and concurrency-safe: the first caller kicks off init, every
     * other caller (the router gating a dropdown-dependent route, a page, the
     * boot sequence) awaits the SAME in-flight promise. Without this guard two
     * callers racing before `initialized` flips would both build the stores and
     * double-fetch the dropdown data.
     */
    async init() {
        if (this.initialized) return;
        if (!this._initPromise) {
            this._initPromise = this._doInit();
        }
        return this._initPromise;
    }

    async _doInit() {
        // Use already-loaded auth.user instead of making another API call
        this.user = auth.user;

        this.stores.set('clients', new ClientStore());
        this.stores.set('groups', new GroupsStore());
        this.stores.set('profiles', new ProfilesStore());
        this.stores.set('notes', new NotesStore());

        const dropdownStore = new DropdownStore();
        this.stores.set('dropdown', dropdownStore);

        try {
            await dropdownStore.init();
        } catch (error) {
            console.error('[StateManager] Dropdown init failed:', error.message);
        }

        this.initialized = true;
    }

    getStore(name) {
        return this.stores.get(name);
    }

    get clients() { return this.stores.get('clients'); }
    get groups() { return this.stores.get('groups'); }
    get profiles() { return this.stores.get('profiles'); }
    get notes() { return this.stores.get('notes'); }
    get dropdown() { return this.stores.get('dropdown'); }

    destroy() {
        this.stores.forEach(store => store.destroy());
        this.stores.clear();
        this.initialized = false;
        this._initPromise = null;
        super.destroy();
    }
}

export const state = new StateManager();
export { ObservableStore, PersistentStore };

if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    window.state = state;
}
